import React, { useState } from "react";
import { Image, View } from "react-native";
import { Box, MainCard, MainText, VideoModal } from "app/components/common";
import {
  isSmallWeb,
  isWeb,
  isWebDesktop,
} from "@microdoing/shared/utils/deviceDetectors";
import { getRate } from "app/utils/getRating";
import i18n from "@microdoing/shared/i18n";
import normalize from "app/utils/normalize";
import FastImage from "react-native-fast-image";
import {
  EmptyStarImage,
  StarImage,
} from "../FeedbackScreen/ReflexiveAnalysis/ActionRatingScreen/RatingStar";
import FilesScrollview from "./FilesScrollview";
import theme from "app/Theme/Theme";
import { useIsFocused } from "@react-navigation/native";
import hasAudioExtension from "@microdoing/shared/utils/hasAudioExtension";
import AudioMessage from "../MessageScreen/ChatScreen/AudioMessage";
import normalizeSource from "app/utils/normalizeSource";
import hasVideoExtension from "@microdoing/shared/utils/hasVideoExtension";
import { useSelector } from "react-redux";
import { getFeedbackSelected } from "@microdoing/shared/services/historical/selector";

interface IProps {}

const AnalysisBox = ({}: IProps) => {
  const [width, setWidth] = useState(300);
  const [videoWidth, setVideoWidth] = useState(normalize(250));

  const isFocused = useIsFocused();

  const { donePercentage, rating, files, comment, _id } = useSelector(
    getFeedbackSelected,
  );

  const findDimensions = ({ width }) => {
    setWidth(width);
  };

  const handleOnLayout = event => {
    findDimensions(event.nativeEvent.layout);
  };

  const handleOnLayoutVideo = event => {
    const { width } = event.nativeEvent.layout;
    setVideoWidth(width);
  };

  const getRatingComponent = () => {
    const rate = getRate(donePercentage);

    return (
      <Box
        flexDirection="row"
        width="100%"
        marginVertical="xs"
        alignItems="center"
      >
        <Box width="40%">
          <MainText variant="l3-bold">
            {i18n.t("recap-feedback-feeling")}
          </MainText>
        </Box>
        <Box
          flex={1}
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <MainText variant="l3-medium" marginLeft="xs">
            {rate.name}
          </MainText>
          <Image
            style={{
              marginRight: normalize(-10),
              width: normalize(40),
              height: normalize(40),
            }}
            source={rate.smiley}
          />
        </Box>
      </Box>
    );
  };

  const getStars = () => {
    let stars: React.ReactNode[];
    stars = [];
    for (let numberOfStar = 1; numberOfStar <= 5; numberOfStar++) {
      stars.push(
        <FastImage
          key={numberOfStar}
          source={numberOfStar <= rating ? StarImage : EmptyStarImage}
          style={{ height: 27, width: 22 }}
        />,
      );
    }
    return stars;
  };

  const getPercentageComponent = () => {
    return (
      <Box flexDirection="row" marginTop="xs">
        <Box width="40%">
          <MainText variant="l3-bold">{i18n.t("recap-feedback-rate")}</MainText>
        </Box>
        <Box
          flex={1}
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {getStars()}
        </Box>
      </Box>
    );
  };

  return (
    <MainCard variant="feedbackCard" flex={1} onLayout={handleOnLayout}>
      <MainText variant="l4-bold" marginTop="sm" marginBottom="xs">
        {i18n.t("reflexive-analysis")}
      </MainText>
      <Box
        marginBottom="sm"
        flex={1}
        width="100%"
        onLayout={handleOnLayoutVideo}
      >
        {(hasVideoExtension(files?.[0]?.uri) || // !!! ? sur le files[0] car le loading n'est pas fait
          hasVideoExtension(files?.[0]?.fileName)) && (
          <VideoModal
            maxHeight={isWebDesktop ? "none" : normalize(150)}
            imagePreview={normalizeSource(files?.[0]?.preview)}
            video={normalizeSource(files?.[0]?.uri || files?.[0]?.fileUrl)}
            source={{
              uri: isWeb
                ? files?.[0]?.uri
                : normalizeSource(files?.[0]?.uri || files?.[0]?.fileUrl),
            }}
            commentaryWidth={videoWidth}
            imagePreviewStyle={{
              width: isWebDesktop ? (videoWidth * 2) / 3 : videoWidth,
              height: isWebDesktop ? "auto" : normalize(150),
              borderRadius: 8,
              maxHeight: isSmallWeb ? 100 : "100%",
            }}
          />
        )}
        {!!hasAudioExtension(files?.[0]?.uri, files?.[0]?.fileName) &&
          isFocused && (
            <AudioMessage
              soundUrl={files?.[0]?.fileUrl || files?.[0]?.uri}
              hasDarkBackground={false}
            />
          )}
        {!!comment && (
          <>
            <MainText variant="l3-medium">{comment}</MainText>
            {files.length > 0 && (
              <FilesScrollview hasCancelButton={false} files={files} />
            )}
          </>
        )}
      </Box>
      <View style={{ marginLeft: -theme.spacing.xm }}>
        <Box
          position="absolute"
          width={width}
          borderColor="neutral.100"
          borderTopWidth={1}
          marginBottom="xs"
        />
      </View>
      {getRatingComponent()}
      {getPercentageComponent()}
    </MainCard>
  );
};

export default AnalysisBox;
